import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { formatVienna } from '../../../../shared/datetime'
import { formatEuro } from '../../../../shared/courses'

import MiniHeader from '../../../../UI/MiniHeader'
import Loading from '../../../../UI/Loading'
import Table from '../../../../UI/Table'
import Btn from '../../../../UI/Btn'

import { certificateFileName, downloadCertificate } from '../../common/Courses/data'

import styles from './style.module.scss'

/**
 * Kurse aus Sicht der Lehrkraft: Wie weit sind die Schüler:innen der
 * betreuten Junior Companies, wer hat die Urkunde — und was bringt das an
 * Gutschrift auf die letzte Rechnung.
 *
 * Die Kurse selbst kann die Lehrkraft ansehen (Vorschau), aber nicht für die
 * Klasse abschließen: Fortschritt und Urkunde gehören der Person, die
 * zugeschaut und geantwortet hat.
 */
export default function TeacherCourses({ eingebettet }) {
	const { t } = useTranslation()
	const history = useHistory()
	const [data, setData] = useState(null)
	const [fehler, setFehler] = useState(false)
	const [holt, setHolt] = useState(null)

	useEffect(() => {
		http.get(TApi.TEACHER_COURSES, { disableToast: true })
			.then(({ data: antwort }) => setData(antwort))
			.catch(() => setFehler(true))
	}, [])

	const kurse = useMemo(() => data?.courses || [], [data])

	const urkunde = (completionId, kursTitel) => {
		setHolt(completionId)
		downloadCertificate(TApi.TEACHER_COURSE_CERTIFICATE(completionId), certificateFileName(kursTitel)).finally(() =>
			setHolt(null)
		)
	}

	const spalten = useMemo(
		() => [
			{ Header: t('Name'), accessor: 'name', width: 180 },
			...kurse.map(kurs => ({
				Header: kurs.title,
				id: `kurs-${kurs.id}`,
				width: 220,
				Cell: ({ row }) => {
					const stand = row.original.courses?.[kurs.id]
					if (!stand) return '—'
					if (stand.completed) {
						return (
							<span className={styles.done}>
								✓ {formatVienna(stand.completedAt, 'dd.MM.yyyy')}{' '}
								{stand.completionId && (
									<Btn
										theme="minimal"
										size="small"
										disabled={holt === stand.completionId}
										onClick={() => urkunde(stand.completionId, kurs.title)}
									>
										{t('Certificate')}
									</Btn>
								)}
							</span>
						)
					}
					// Kurse von TalentsLounge: Der Fortschritt liegt dort, hier kommt nur der Abschluss an.
					if (kurs.provider === 'TL') return t('not yet completed')
					return t('{{lessons}} of {{total}} lessons · {{modules}} of {{mtotal}} quizzes', {
						lessons: stand.lessonsDone || 0,
						total: stand.lessonsTotal || 0,
						modules: stand.modulesPassed || 0,
						mtotal: stand.modulesTotal || 0
					})
				}
			}))
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[kurse, holt]
	)

	return (
		<div className={styles.root}>
			{eingebettet ? (
				<h2 className={styles.section}>{t('Courses of your Junior Companies')}</h2>
			) : (
				<MiniHeader title={t('Courses')} bg="#f8faff" />
			)}

			<p className={styles.intro}>
				{t(
					'Here you can see how far the students of your Junior Companies have got. You can watch the courses yourself in the preview — progress and certificates belong to the students.'
				)}
			</p>

			{kurse.some(kurs => kurs.provider !== 'TL') && (
				<div className={styles.previews}>
					{kurse.filter(kurs => kurs.provider !== 'TL').map(kurs => (
						<Btn key={kurs.id} theme="secondary" onClick={() => history.push(TRoutes.COURSE(kurs.id))}>
							{t('Watch „{{title}}“ (preview)', { title: kurs.title })}
						</Btn>
					))}
				</div>
			)}

			{!data && !fehler && <Loading />}
			{fehler && <p className={styles.empty}>{t('The course overview could not be loaded.')}</p>}

			{data && (data.companies || []).length === 0 && (
				<p className={styles.empty}>{t('As soon as you supervise a Junior Company, its students appear here.')}</p>
			)}

			{(data?.companies || []).map(company => (
				<section key={company.id} className={styles.company}>
					<div className={styles.companyHead}>
						<h2 className={styles.companyName}>
							{company.name} <span className={styles.type}>{company.companyType}</span>
						</h2>
						{company.credit?.enabled && (
							<div className={styles.credit}>
								{/* Der Server liefert offen (`pending`) und schon verrechnet (`applied`) getrennt. */}
								<b>{formatEuro((company.credit.pending || 0) + (company.credit.applied || 0))}</b>{' '}
								{t('credit from {{n}} completions', { n: company.credit.count || 0 })}
								{company.credit.cap ? ` · ${t('cap')} ${formatEuro(company.credit.cap)}` : ''}
								<span className={styles.creditHint}>
									{t('Deducted from the last invoice of the school year.')}
								</span>
							</div>
						)}
					</div>
					<div className="container">
						<Table columns={spalten} items={company.students || []} />
					</div>
				</section>
			))}
		</div>
	)
}

TeacherCourses.propTypes = {
	// Eingebettet in die Seite „Kurse" (views/common/TlCourses): Abschnittsüberschrift statt Seitenkopf.
	eingebettet: PropTypes.bool
}

TeacherCourses.defaultProps = { eingebettet: false }
